


































































































import { Vue, Component, Watch } from "vue-property-decorator";
import { State } from "vuex-class";
import moment from "moment";
import LeadPanel from "@/components/Lead/LeadPanel.vue";
import VerificationClaim from "@/components/Lead/VerificationClaim.vue";
import VerificationClaimAdmin from "@/components/Lead/VerificationClaimAdmin.vue";
import { firestore, collections } from "@/configs/firebase";
import logger from "@/configs/logger";
import { User } from "@/types";
import { objectify } from "@/helpers";
import { differenceBy, uniqBy } from "lodash";

type Panel = number | undefined | null;

@Component({
  components: { LeadPanel, VerificationClaim, VerificationClaimAdmin }
})
export default class Verification extends Vue {
  @State("user") user!: User;

  unsubscribe!: Function;
  loading: boolean = true;
  panel: Panel = null;
  pendingVerifications: Array<any> = [];
  pendingVersToAdd: Array<any> = [];
  pendingVersToRemove: any[] = [];
  initialDataFetch: boolean = true;
  verAgents: Array<any> = [];

  @Watch("panel")
  onPanelChange(val: Panel, prev: Panel) {
    const panelIsClosed = typeof val !== "number";
    if (panelIsClosed) {
      this.flushStackedVers();
    }
  }

  get isVerAdmin(): boolean {
    return (
      this.user.roles.includes("admin") ||
      this.user.groups.includes("verification_supervisor")
    );
  }

  created() {
    this.getPendingVerification();
    if (this.isVerAdmin) {
      collections.user
        .where("roles", "array-contains", "verification")
        .get()
        .then(snapshot => {
          this.verAgents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        })
        .catch(err => {
          logger("error", "failed to fetch ver agents:", err);
        });
    }
  }

  beforeDestroy() {
    this.unsubscribe();
  }

  getAssignedCloser(lead: any) {
    if (lead) {
      const closer = objectify(lead.assigned_agent_email_by_role).closer;
      if (closer) {
        return closer;
      }
    }
    return "n/a";
  }

  canOpenPanel(lead: any) {
    if (this.user.roles.includes("admin")) {
      return true;
    }
    if (!lead.verification_agent_email) {
      return true;
    }
    if (this.user.email === lead.verification_agent_email) {
      return true;
    }
    return false;
  }

  hasPostDate(lead: any) {
    if (lead.products && objectify(lead.products.health).post_date) {
      return true;
    }
    return false;
  }

  hasDiscountPlan(lead: any) {
    if (lead.products && lead.products.discount_plan) {
      return true;
    }
    return false;
  }

  getCardColor(claimed = false, lead: any) {
    if (claimed && lead.verification_agent_email === this.user.email) {
      return "amber accent-2";
    }
    if (!claimed) {
      return "red lighten-3";
    }
    return "white";
  }

  getPostdateDate(lead: any) {
    if (lead.products && objectify(lead.products.health).post_date) {
      return lead.products.health.post_date;
    }
    return "";
  }

  addPendingVers(doc: any) {
    if (typeof this.panel !== "number") {
      const pendingVers = this.pendingVerifications.filter(
        item => item.id !== doc.id
      );
      this.pendingVerifications = [doc, ...pendingVers];
    } else {
      const itemsToAdd = this.pendingVersToAdd.filter(
        item => item.id !== doc.id
      );
      itemsToAdd.push(doc);
      this.pendingVersToAdd = itemsToAdd;
    }
  }

  removeFromPendingVers(doc: any) {
    if (typeof this.panel !== "number") {
      this.pendingVerifications = this.pendingVerifications.filter(
        item => item.id !== doc.id
      );
    } else {
      this.pendingVersToRemove.push(doc);
    }
  }

  updatePendingVers(doc: any) {
    const updatedItemIndex = this.pendingVerifications.findIndex(
      item => item.id == doc.id
    );
    if (updatedItemIndex !== -1) {
      this.pendingVerifications = Object.assign([], this.pendingVerifications, {
        [updatedItemIndex]: doc
      });
    }
  }

  flushStackedVers() {
    if (this.pendingVersToAdd.length || this.pendingVersToRemove.length) {
      let vers = this.pendingVerifications.slice();
      vers = [...this.pendingVersToAdd.reverse(), ...vers];
      vers = differenceBy(vers, this.pendingVersToRemove, "id");
      this.pendingVerifications = vers;
      // function might be the cause of multiple instances of the same verification -- for bug tracking
      logger("log", "flushStackedVers add", this.pendingVersToAdd);
      logger("log", "flushStackedVers remove", this.pendingVersToRemove);
      logger("log", "flushStackedVers update", vers);
    }
  }

  getLeadDisplayId(lead: any): any {
    const firstName = lead.first_name || "";
    const lastName = lead.last_name || "";
    const phoneNumber = lead.phone_number || "";
    const leadSource = lead.lead_source || "";
    return `${firstName} ${lastName} ${lead.lead_source}`;
  }

  getRequestTime(lead: any): string {
    return moment.unix(lead.verification_requested_ts.seconds).fromNow();
  }

  getPendingVerification() {
    this.loading = true;
    this.unsubscribe && this.unsubscribe();
    this.unsubscribe = collections.lead
      .where("verification_requested", "==", true)
      .where("verification_completed", "==", false)
      .orderBy("verification_requested_ts", "desc")
      .onSnapshot(snapshot => {
        if (this.initialDataFetch) {
          const data = snapshot.docs.map(item => ({
            id: item.id,
            ...item.data()
          }));
          this.pendingVerifications = data;
          this.loading = false;
          this.initialDataFetch = false;
        } else {
          logger("log", "not initial data fetch.");
          // prevent buggines when index of documents change
          snapshot.docChanges().forEach(change => {
            const doc = { id: change.doc.id, ...change.doc.data() };
            if (change.type == "added") {
              this.addPendingVers(doc);
            } else if (change.type == "removed") {
              this.removeFromPendingVers(doc);
            } else {
              this.updatePendingVers(doc);
            }
          });
        }
      });
  }
}
